import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { t } from 'i18next'

function CustomerDisplayBranchSelector({
  value,
  options,
  disabled,
  error,
  helperText,
  onChange,
  id,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  const selectedBranchIds = value || []
  const selectedBranches = options.filter(branch =>
    selectedBranchIds.includes(branch.BranchOfficeId)
  )

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleToggleBranch = branchId => {
    const nextValue = selectedBranchIds.includes(branchId)
      ? selectedBranchIds.filter(selectedId => selectedId !== branchId)
      : [...selectedBranchIds, branchId]

    onChange(nextValue)
  }

  const selectedLabel =
    selectedBranches.length === 0
      ? t('CUSTOMER_DISPLAY_SETTINGS.FORM.BRANCH_OFFICES')
      : selectedBranches.length === 1
      ? selectedBranches[0].Description
      : `${selectedBranches.length} ${t(
          'CUSTOMER_DISPLAY_SETTINGS.FORM.BRANCH_OFFICES'
        )}`

  const selectedTitle = selectedBranches
    .map(branch => branch.Description)
    .join(', ')

  const listId = `select-${id}-options`

  return (
    <div
      className="customer-display-branch-selector"
      ref={containerRef}
      data-testid={`select-${id}`}>
      <button
        id={`select-${id}`}
        type="button"
        className={`customer-display-branch-selector-header${
          disabled ? ' disabled' : ''
        }${error ? ' error' : ''}`}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-invalid={error || undefined}
        onClick={() => setIsOpen(!isOpen)}>
        <span
          className={`customer-display-branch-label${
            selectedBranches.length === 0
              ? ' customer-display-branch-placeholder'
              : ''
          }`}
          title={selectedTitle}>
          {selectedLabel}
        </span>
        <FontAwesomeIcon
          className={`customer-display-branch-chevron${isOpen ? ' open' : ''}`}
          icon={faChevronDown}
        />
      </button>

      {isOpen && (
        <ul
          id={listId}
          className="customer-display-branch-selector-list"
          role="listbox"
          aria-multiselectable="true"
          data-testid={`select-${id}-options`}>
          {options.length > 0 ? (
            options.map((branch, index) => {
              const branchId = branch.BranchOfficeId
              const isSelected = selectedBranchIds.includes(branchId)
              const inputId = `customer-display-branch-${id}-${
                branchId || index
              }`

              return (
                <li
                  key={branchId || index}
                  className={`customer-display-branch-selector-item${
                    isSelected ? ' selected' : ''
                  }`}
                  role="option"
                  aria-selected={isSelected}
                  data-testid={`select-${id}-option-${branchId}`}
                  onClick={() => handleToggleBranch(branchId)}>
                  <label
                    htmlFor={inputId}
                    onClick={event => event.stopPropagation()}>
                    <input
                      id={inputId}
                      type="checkbox"
                      className="customer-display-branch-checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleBranch(branchId)}
                    />
                    <span>{branch.Description}</span>
                  </label>
                </li>
              )
            })
          ) : (
            <li className="customer-display-branch-selector-item disabled">
              <span>
                {t('CUSTOMER_DISPLAY_SETTINGS.FORM.NO_OPTIONS_AVAILABLE')}
              </span>
            </li>
          )}
        </ul>
      )}

      {helperText && (
        <small className={error ? 'input-feedback' : 'text-muted'}>
          {helperText}
        </small>
      )}

      {options.length > 0 && !disabled && (
        <div className="customer-display-branch-actions">
          <button
            type="button"
            className="customer-display-branch-action"
            onClick={() =>
              onChange(options.map(branch => branch.BranchOfficeId))
            }>
            {t('CUSTOMER_DISPLAY_SETTINGS.FORM.SELECT_ALL')}
          </button>

          <button
            type="button"
            className="customer-display-branch-action clear"
            onClick={() => onChange([])}
            disabled={selectedBranchIds.length === 0}>
            {t('CUSTOMER_DISPLAY_SETTINGS.FORM.CLEAR_ALL')}
          </button>
        </div>
      )}
    </div>
  )
}

export default CustomerDisplayBranchSelector
